// BUILD=R32-selection-images-expand-stability
import { useEffect, useMemo, useRef, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Loader2, Printer, FileCheck2, FileSpreadsheet, Download } from "lucide-react";
import { generateSikkensCenterExcel, SIKKENS_CENTER_SUPPLIER } from "@/lib/exportSikkensCenter";
import { useSikkensExportLog, formatExportedAt } from "@/hooks/useSikkensExportLog";
import type { BadgeItemMeta } from "./BadgeRow";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/context/PermissionsContext";
import {
  useOrdersDecoprof,
  useOrdersDecoprofFlash,
  useUpdateOrdersDecoprofStatus,
  type OrdersDecoprofRow,
} from "@/hooks/useOrdersDecoprof";
import {
  ORDER_DECOPROF_TYPE_OPTIONS,
  ORDER_DECOPROF_STATUSES,
  KNOWN_SUPPLIERS,
  printPackingSlipsBatch,
  getOrderTypes,
  getOrderSuppliers,
  type OrderDecoprofType,
  type OrderDecoprofStatus,
  isMobileDevice,
} from "@/lib/ordersDecoprof";
import BadgeStatus3D from "./BadgeStatus3D";
import BadgeRow from "./BadgeRow";
import Card3D from "./Card3D";
import OrderLinesExpansion from "./OrderLinesExpansion";
import Button3D from "./Button3D";
import Tile3DIconButton from "./Tile3DIconButton";
import Filter3DDropdown from "./Filter3DDropdown";
import ScrollHelpers from "./ScrollHelpers";
import ExportConfirmDialog from "./ExportConfirmDialog";
import { usePrefetchOnVisible } from "@/hooks/usePrefetchOnVisible";

// R45: kleine sub-component zodat we per kaart een ref kunnen koppelen aan de
// IntersectionObserver-prefetch zonder Card3D te hoeven wijzigen.
const PrefetchSentinel = ({ orderid }: { orderid: number | null }) => {
  const ref = usePrefetchOnVisible(orderid);
  return <div ref={ref} aria-hidden className="h-0 w-0" />;
};

// R72: NL-formaat "5 mei 2026, 14:32" voor orderdatum+tijd in overzichtsregel.
const ORDER_DATE_FMT = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric", month: "long", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});
const formatOrderDateTime = (iso: string): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  // Intl voegt " om " in tussen datum en tijd; vervang door ", " voor compacte weergave
  return ORDER_DATE_FMT.format(new Date(t)).replace(" om ", ", ");
};

const STATUS_PILL_COLOR: Record<string, string> = {
  Nieuw: "bg-blue-500 text-white ring-blue-300/50",
  Uitgeprint: "bg-emerald-500 text-white ring-emerald-300/50",
  Aangemaakt: "bg-violet-500 text-white ring-violet-300/50",
};
const TYPE_PILL_COLOR: Record<string, string> = {
  Verfbestelling: "bg-orange-500 text-white ring-orange-300/50",
  Behangorder: "bg-teal-500 text-white ring-teal-300/50",
  Spuitbusbestelling: "bg-amber-500 text-white ring-amber-300/50",
  Overige: "bg-pink-500 text-white ring-pink-300/50",
};

const OrdersDecoprofTab = () => {
  const { data: orders, isLoading, error } = useOrdersDecoprof();
  const { data: sikkensExportMap } = useSikkensExportLog();
  const flashing = useOrdersDecoprofFlash();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<OrderDecoprofType[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderDecoprofStatus[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
  // R71: validatie-filter — toont alleen orders met 2+ types (mixed orders).
  const [multiTypeOnly, setMultiTypeOnly] = useState<boolean>(false);
  // R75: validatie-filter — toont alleen orders met 2+ unieke leveranciers.
  const [multiSupplierOnly, setMultiSupplierOnly] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [printFailed, setPrintFailed] = useState<Set<string>>(new Set());
  const [printingOrders, setPrintingOrders] = useState<Set<string>>(new Set());
  const [printErrorReasons, setPrintErrorReasons] = useState<Record<string, string>>({});
  const lastClickedRef = useRef<string | null>(null);

  const allSuppliers = useMemo(() => {
    const set = new Set<string>(KNOWN_SUPPLIERS);
    (orders ?? []).forEach((o) => o.suppliers.forEach((s) => set.add(s)));
    // R77: "Sikkens Center en Simonis" altijd bovenaan, rest alfabetisch (nl).
    return Array.from(set).sort((a, b) => {
      if (a === SIKKENS_CENTER_SUPPLIER && b !== SIKKENS_CENTER_SUPPLIER) return -1;
      if (b === SIKKENS_CENTER_SUPPLIER && a !== SIKKENS_CENTER_SUPPLIER) return 1;
      return a.localeCompare(b, "nl");
    });
  }, [orders]);

  // R71: aantal multi-type orders in de huidige geladen data (vóór filters),
  // gebruikt voor de teller in de toggle-knop.
  const multiTypeCount = useMemo(
    () => (orders ?? []).filter((o) => getOrderTypes(o.suppliers).length > 1).length,
    [orders],
  );
  // R75: aantal multi-leverancier orders in de huidige geladen data (vóór filters).
  const multiSupplierCount = useMemo(
    () => (orders ?? []).filter((o) => getOrderSuppliers(o.suppliers).length > 1).length,
    [orders],
  );

  const filteredOrders = useMemo(() => {
    let list = orders ?? [];
    // R57 — FIX 4: type-filter matched op ELK type van de order (mixed orders).
    if (typeFilter.length > 0) {
      list = list.filter((o) =>
        getOrderTypes(o.suppliers).some((t) => typeFilter.includes(t)),
      );
    }
    if (statusFilter.length > 0) list = list.filter((o) => statusFilter.includes(o.status));
    if (supplierFilter.length > 0) {
      list = list.filter((o) => o.suppliers.some((s) => supplierFilter.includes(s)));
    }
    // R71: multi-type validatie-filter (combineerbaar met andere filters).
    if (multiTypeOnly) {
      list = list.filter((o) => getOrderTypes(o.suppliers).length > 1);
    }
    // R75: multi-leverancier validatie-filter (combineerbaar met andere filters).
    if (multiSupplierOnly) {
      list = list.filter((o) => getOrderSuppliers(o.suppliers).length > 1);
    }
    return list;
  }, [orders, typeFilter, statusFilter, supplierFilter, multiTypeOnly, multiSupplierOnly]);

  // Selection helpers
  const visibleOrdernumbers = useMemo(
    () => filteredOrders.map((o) => o.ordernumber),
    [filteredOrders],
  );
  const visibleSelectedCount = useMemo(
    () => visibleOrdernumbers.filter((n) => selected.has(n)).length,
    [visibleOrdernumbers, selected],
  );
  const hiddenSelectedCount = selected.size - visibleSelectedCount;
  const masterState: boolean | "indeterminate" =
    visibleOrdernumbers.length > 0 && visibleSelectedCount === visibleOrdernumbers.length
      ? true
      : visibleSelectedCount > 0
        ? "indeterminate"
        : false;

  const toggleMaster = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (visibleSelectedCount === visibleOrdernumbers.length) {
        // deselect all visible
        visibleOrdernumbers.forEach((n) => next.delete(n));
      } else {
        visibleOrdernumbers.forEach((n) => next.add(n));
      }
      return next;
    });
  };

  const toggleRow = (ordernumber: string, e?: React.MouseEvent | React.KeyboardEvent) => {
    const shift = (e as React.MouseEvent | undefined)?.shiftKey;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastClickedRef.current && lastClickedRef.current !== ordernumber) {
        const a = visibleOrdernumbers.indexOf(lastClickedRef.current);
        const b = visibleOrdernumbers.indexOf(ordernumber);
        if (a !== -1 && b !== -1) {
          const [from, to] = a < b ? [a, b] : [b, a];
          for (let i = from; i <= to; i++) next.add(visibleOrdernumbers[i]);
          lastClickedRef.current = ordernumber;
          return next;
        }
      }
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      lastClickedRef.current = ordernumber;
      return next;
    });
  };

  

  const toggleExpand = (ordernumber: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  };

  const selectedRows: OrdersDecoprofRow[] = useMemo(
    () => (orders ?? []).filter((o) => selected.has(o.ordernumber) && o.orderid != null),
    [orders, selected],
  );

  const canPrintPackingSlip = hasPermission("ordersDecoprof", "read");
  const hasSelection = selected.size >= 1;
  const printDisabled = !canPrintPackingSlip || !hasSelection;
  const printDisabledReason = !canPrintPackingSlip
    ? "Geen toegang tot Orders Decoprof"
    : !hasSelection
      ? "Selecteer minimaal 1 order"
      : "";

  const [isPrinting, setIsPrinting] = useState(false);
  const [isMarkingCreated, setIsMarkingCreated] = useState(false);

  const removeFromSelection = (ordernumbers: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ordernumbers.forEach((n) => next.delete(n));
      return next;
    });
  };

  const runPrint = async (rows: OrdersDecoprofRow[], preOpenedWindow?: Window | null) => {
    if (rows.length === 0) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      return;
    }
    const orderids = rows.map((o) => o.orderid as number);
    const result = await printPackingSlipsBatch(orderids, preOpenedWindow);

    const succeededRows = rows.filter((o) =>
      result.succeededOrderids.includes(o.orderid as number),
    );
    const failedRows = rows.filter((o) =>
      result.failedOrderids.includes(o.orderid as number),
    );

    if (succeededRows.length > 0) {
      try {
        await updateStatus.mutateAsync({
          ordernumbers: succeededRows.map((o) => o.ordernumber),
          status: "Uitgeprint",
        });
        removeFromSelection(succeededRows.map((o) => o.ordernumber));
      } catch (e: any) {
        toast.warning(`PDF gedownload, status-update faalde: ${e?.message ?? e}`);
      }
    }

    setPrintFailed((prev) => {
      const next = new Set(prev);
      succeededRows.forEach((r) => next.delete(r.ordernumber));
      failedRows.forEach((r) => next.add(r.ordernumber));
      return next;
    });

    if (failedRows.length === 0) {
      toast.success(
        `Pakbon-PDF gedownload — ${succeededRows.length} order${succeededRows.length === 1 ? "" : "s"} → Uitgeprint`,
      );
    } else {
      const reasonStr = Object.entries(result.failedReasons)
        .map(([id, r]) => `${id}: ${r}`).join("; ");
      toast.warning(
        `${succeededRows.length} ok, ${failedRows.length} mislukt (${failedRows.map((r) => r.ordernumber).join(", ")})${reasonStr ? ` — ${reasonStr}` : ""}`,
      );
    }
  };

  const handlePrint = async () => {
    if (selectedRows.length === 0) return;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "(no-ua)";
    const ids = selectedRows.map((o) => o.orderid as number);
    console.log(`[R45 batch-print] click — selected=${selected.size} rows=${selectedRows.length} ids=[${ids.join(",")}] ua="${ua}"`);
    // R45: open SYNCHROON een tabblad binnen de user-gesture op mobiel,
    // anders blokkeert iOS Safari de latere window.open na await.
    let preOpenedWindow: Window | null = null;
    if (isMobileDevice()) {
      try {
        preOpenedWindow = window.open("about:blank", "_blank");
      } catch { preOpenedWindow = null; }
    }
    setIsPrinting(true);
    try {
      await runPrint(selectedRows, preOpenedWindow);
    } catch (e: any) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      console.error("[batch-print] error", e);
      const reasons = e?.failedReasons as Record<string, string> | undefined;
      const detail = reasons && Object.keys(reasons).length > 0
        ? ` — ${Object.entries(reasons).map(([id, r]) => `${id}: ${r}`).join("; ")}`
        : "";
      toast.error(`Pakbon afdrukken mislukt: ${e?.message ?? e}${detail}`);
      setPrintFailed((prev) => {
        const next = new Set(prev);
        selectedRows.forEach((r) => next.add(r.ordernumber));
        return next;
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handleRetryPrint = async (row: OrdersDecoprofRow) => {
    if (row.orderid == null) return;
    setIsPrinting(true);
    try {
      await runPrint([row]);
    } catch (e: any) {
      toast.error(`Retry mislukt: ${e?.message ?? e}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // R50: single-order print vanaf de tegel-knop. Gebruikt dezelfde pipeline
  // als bulk maar met 1 element. iOS: open synchroon een leeg tabblad in de
  // user-gesture (R45 fix) en geef het door aan printPackingSlipsBatch.
  const handleTilePrint = async (row: OrdersDecoprofRow) => {
    if (row.orderid == null) return;
    if (!canPrintPackingSlip) return;
    if (printingOrders.has(row.ordernumber)) return;

    let preOpenedWindow: Window | null = null;
    if (isMobileDevice()) {
      try { preOpenedWindow = window.open("about:blank", "_blank"); }
      catch { preOpenedWindow = null; }
    }
    setPrintingOrders((prev) => {
      const next = new Set(prev); next.add(row.ordernumber); return next;
    });
    try {
      await runPrint([row], preOpenedWindow);
      setPrintErrorReasons((prev) => {
        if (!(row.ordernumber in prev)) return prev;
        const next = { ...prev }; delete next[row.ordernumber]; return next;
      });
    } catch (e: any) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      const reasons = e?.failedReasons as Record<string, string> | undefined;
      const reason = reasons && row.orderid != null ? reasons[String(row.orderid)] : undefined;
      const msg = reason ?? e?.message ?? String(e);
      toast.error(`Pakbon ${row.ordernumber} mislukt: ${msg}`);
      setPrintFailed((prev) => { const next = new Set(prev); next.add(row.ordernumber); return next; });
      setPrintErrorReasons((prev) => ({ ...prev, [row.ordernumber]: msg }));
    } finally {
      setPrintingOrders((prev) => {
        const next = new Set(prev); next.delete(row.ordernumber); return next;
      });
    }
  };

  const handleMarkCreated = async () => {
    if (selectedRows.length === 0) return;
    setIsMarkingCreated(true);
    try {
      const ordernumbers = selectedRows.map((o) => o.ordernumber);
      await updateStatus.mutateAsync({ ordernumbers, status: "Aangemaakt" });
      toast.success(`${ordernumbers.length} order${ordernumbers.length === 1 ? "" : "s"} → Aangemaakt`);
      removeFromSelection(ordernumbers);
    } catch (e: any) {
      toast.error(`Status update mislukt: ${e?.message ?? e}`);
    } finally {
      setIsMarkingCreated(false);
    }
  };

  // R77 — knop "Sikkens Center bestellijst" verschijnt zodra ≥1 geselecteerde order
  // de leverancier "Sikkens Center en Simonis" bevat.
  const sikkensSelectedCount = useMemo(() => {
    if (selected.size === 0) return 0;
    return filteredOrders.reduce(
      (n, o) =>
        selected.has(o.ordernumber) &&
        getOrderSuppliers(o.suppliers).includes(SIKKENS_CENTER_SUPPLIER)
          ? n + 1
          : n,
      0,
    );
  }, [selected, filteredOrders]);
  const selectionContainsSikkensCenter = sikkensSelectedCount > 0;

  const [isExportingSikkens, setIsExportingSikkens] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // R77 — splits geselecteerde Sikkens-orders in nieuw vs al-geëxporteerd.
  const sikkensSelectionPartition = useMemo(() => {
    const all: string[] = [];
    const fresh: string[] = [];
    const exported: string[] = [];
    for (const o of filteredOrders) {
      if (!selected.has(o.ordernumber)) continue;
      if (!getOrderSuppliers(o.suppliers).includes(SIKKENS_CENTER_SUPPLIER)) continue;
      all.push(o.ordernumber);
      const isExported = o.orderid != null && sikkensExportMap?.has(o.orderid);
      if (isExported) exported.push(o.ordernumber);
      else fresh.push(o.ordernumber);
    }
    return { all, fresh, exported };
  }, [filteredOrders, selected, sikkensExportMap]);

  const runSikkensExport = async (ordernumbers: string[]) => {
    if (ordernumbers.length === 0) return;
    setIsExportingSikkens(true);
    try {
      const res = await generateSikkensCenterExcel(ordernumbers);
      toast.success(
        `Excel gedownload — ${res.rowCount} regel(s), ${res.orderCount} order(s)`,
      );
    } catch (e: any) {
      toast.error(`Sikkens Center export mislukt: ${e?.message ?? e}`);
    } finally {
      setIsExportingSikkens(false);
    }
  };

  const handleExportSikkens = async () => {
    const { all, exported } = sikkensSelectionPartition;
    if (all.length === 0) return;
    if (exported.length === 0) {
      await runSikkensExport(all);
      return;
    }
    setExportDialogOpen(true);
  };


  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-start gap-3">
        <Filter3DDropdown
          label="Type order"
          options={ORDER_DECOPROF_TYPE_OPTIONS as readonly string[]}
          values={typeFilter}
          onChange={(v) => setTypeFilter(v as OrderDecoprofType[])}
          pillColor={(v) => TYPE_PILL_COLOR[v] ?? ""}
        />
        <Filter3DDropdown
          label="Status"
          options={ORDER_DECOPROF_STATUSES as readonly string[]}
          values={statusFilter}
          onChange={(v) => setStatusFilter(v as OrderDecoprofStatus[])}
          pillColor={(v) => STATUS_PILL_COLOR[v] ?? ""}
        />
        <Filter3DDropdown
          label="Leverancier"
          options={allSuppliers}
          values={supplierFilter}
          onChange={setSupplierFilter}
          searchable
          searchPlaceholder="Zoek leverancier..."
          emptySearchText="Geen leveranciers gevonden"
        />
        {/* R71: validatie-toggle — alleen orders met 2+ types */}
        <button
          type="button"
          onClick={() => setMultiTypeOnly((v) => !v)}
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-3 rounded-full text-sm font-medium transition-all duration-150",
            "ring-1 ring-slate-300/60 hover:-translate-y-0.5",
            multiTypeOnly
              ? "bg-gradient-to-b from-pink-500 to-pink-600 text-white ring-pink-300/50 shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.3)]"
              : "bg-gradient-to-b from-white to-slate-100 text-slate-700 shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
          )}
          title="Toon alleen orders met meerdere types (validatie)"
        >
          Multi-type orders
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold h-5 min-w-[1.25rem] px-1.5",
              multiTypeOnly ? "bg-white/25 text-white" : "bg-slate-700 text-white",
            )}
          >
            {multiTypeCount}
          </span>
        </button>

        {/* R75: validatie-toggle — alleen orders met 2+ unieke leveranciers */}
        <button
          type="button"
          onClick={() => setMultiSupplierOnly((v) => !v)}
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-3 rounded-full text-sm font-medium transition-all duration-150",
            "ring-1 ring-slate-300/60 hover:-translate-y-0.5",
            multiSupplierOnly
              ? "bg-gradient-to-b from-slate-700 to-slate-800 text-white ring-slate-400/50 shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.2)]"
              : "bg-gradient-to-b from-white to-slate-100 text-slate-700 shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
          )}
          title="Toon alleen orders met meerdere leveranciers (validatie)"
        >
          Multi-leverancier orders
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold h-5 min-w-[1.25rem] px-1.5",
              multiSupplierOnly ? "bg-white/25 text-white" : "bg-slate-700 text-white",
            )}
          >
            {multiSupplierCount}
          </span>
        </button>

        <div className="ml-auto flex gap-2 flex-wrap">
          {/* R77: Excel-export Sikkens Center bestellijst (verschijnt bij selectie met ≥1 Sikkens Center order) */}
          <TooltipProvider>
            {selectionContainsSikkensCenter && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button3D
                      tone="primary"
                      onClick={handleExportSikkens}
                      disabled={isExportingSikkens}
                    className="sm:min-w-[18rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isExportingSikkens ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      <span className="tabular-nums">{sikkensSelectedCount}</span>
                      {sikkensSelectedCount === 1
                        ? " Sikkens Center order exporteren"
                        : " Sikkens Center orders exporteren"}
                    </span>
                    <span className="sm:hidden pointer-events-none">
                      XLS (<span className="tabular-nums">{sikkensSelectedCount}</span>)
                    </span>
                    </Button3D>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Download Excel met alleen de Trimetal/SPS-regels van geselecteerde orders
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button3D
                    tone="violet"
                    onClick={handleMarkCreated}
                    disabled={printDisabled || updateStatus.isPending || isMarkingCreated || isPrinting}
                    className="sm:min-w-[14rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isMarkingCreated ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCheck2 className="h-4 w-4" />
                    )}
                    {/* R43: pointer-events-none op alle inner spans → klik landt altijd op <button>, niet op inner text. */}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      {selected.size === 0 ? (
                        "Aangemaakt"
                      ) : (
                        <>
                          <span className="tabular-nums">{selected.size}</span>
                          {selected.size === 1 ? " order op Aangemaakt" : " orders op Aangemaakt"}
                        </>
                      )}
                    </span>
                    <span className="sm:hidden pointer-events-none tabular-nums">
                      {selected.size === 0 ? "" : selected.size}
                    </span>
                  </Button3D>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button3D
                    tone="primary"
                    onClick={handlePrint}
                    disabled={printDisabled || updateStatus.isPending || isPrinting || isMarkingCreated}
                    className="sm:min-w-[13rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      {selected.size === 0 ? (
                        "Pakbon afdrukken"
                      ) : (
                        <>
                          <span className="tabular-nums">{selected.size}</span>
                          {selected.size === 1 ? " order afdrukken" : " orders afdrukken"}
                        </>
                      )}
                    </span>
                    <span className="sm:hidden pointer-events-none tabular-nums">
                      {selected.size === 0 ? "" : selected.size}
                    </span>
                  </Button3D>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {!isLoading && (
        <p className="text-sm text-muted-foreground">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} gevonden
        </p>
      )}

      {/* Header-rij met master-checkbox + altijd-zichtbare selectie-teller (vaste hoogte → geen layout shift) */}
      <div className="hidden md:grid grid-cols-[40px_1fr_180px_1fr_140px_36px] gap-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground items-center">
        <div>
          <Checkbox
            checked={masterState}
            onCheckedChange={toggleMaster}
            aria-label="Selecteer alle zichtbare orders"
            disabled={visibleOrdernumbers.length === 0}
          />
        </div>
        <div className="flex items-center gap-3">
          <span>Ordernummer</span>
          {/* R33: vaste reserved space — inline-block + min-w + nbsp placeholder
              voorkomt dat de regel pas hoogte krijgt zodra tekst verschijnt. */}
          <span className="inline-block min-w-[14rem] h-5 leading-5 text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
            {selected.size > 0 ? (
              <>
                {selected.size} geselecteerd
                {hiddenSelectedCount > 0 && `, ${hiddenSelectedCount} verborgen door filter`}
              </>
            ) : (
              "\u00A0"
            )}
          </span>
        </div>
        <div>Type order</div>
        <div>Leverancier</div>
        <div>Status</div>
        <div></div>
      </div>

      <div className="space-y-2">
        {isLoading ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
            Laden...
          </Card3D>
        ) : error ? (
          <Card3D className="px-4 py-8 text-center text-destructive">
            Fout bij laden van orders
          </Card3D>
        ) : filteredOrders.length === 0 ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            {multiSupplierOnly
              ? "Geen multi-leverancier orders gevonden in de huidige weergave"
              : multiTypeOnly
                ? "Geen multi-type orders gevonden in de huidige weergave"
                : "Geen Decoprof-orders gevonden"}
          </Card3D>
        ) : (
          (() => {
            const renderOrder = (order: OrdersDecoprofRow) => {
            const isExpanded = expanded.has(order.ordernumber);
            const isFailed = printFailed.has(order.ordernumber);
            const isFlashing = flashing.has(order.ordernumber);
            // R77: Sikkens Center badge → groen accent + download-icoon + tooltip met export-datum.
            const sikkensExportedAt = order.orderid != null
              ? sikkensExportMap?.get(order.orderid)
              : undefined;
            const supplierMeta: Record<string, BadgeItemMeta> | undefined = sikkensExportedAt
              ? {
                  [SIKKENS_CENTER_SUPPLIER]: {
                    toneClass: "bg-emerald-50 text-emerald-800 ring-emerald-200",
                    icon: <Download className="h-3 w-3" />,
                    tooltip: `Geëxporteerd op ${formatExportedAt(sikkensExportedAt)}`,
                  },
                }
              : undefined;
            return (
              <Card3D
                key={order.ordernumber}
                className={cn(
                  "transition-transform duration-150 hover:-translate-y-0.5 cursor-pointer overflow-hidden",
                  isFailed && "border-2 border-red-500/60",
                  isFlashing && "animate-[flashIn_1.5s_ease-out] ring-2 ring-primary/40",
                )}
                onClick={(e) => {
                  toggleExpand(order.ordernumber);
                  // Houd de geklikte tegel zichtbaar — voorkom dat hij uit beeld duikt
                  const el = (e.currentTarget as HTMLElement);
                  requestAnimationFrame(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                  });
                }}
                title={isFailed ? "Niet afgedrukt — klik op de retry-knop om opnieuw te proberen" : undefined}
              >
                <PrefetchSentinel orderid={order.orderid} />
                {/* R75: items-start + vaste min-h zorgt dat rijen met 1 of meerdere badges dezelfde hoogte hebben en cellen vanaf bovenkant uitlijnen. */}
                <div className="grid grid-cols-[40px_1fr_auto] md:grid-cols-[40px_1fr_180px_1fr_140px_36px] items-start md:min-h-[64px] gap-3 px-4 py-2 md:py-3">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(order.ordernumber)}
                      onClick={(e) => toggleRow(order.ordernumber, e as any)}
                      onCheckedChange={() => {
                        if (lastClickedRef.current === order.ordernumber) return;
                      }}
                      aria-label={`Selecteer order ${order.ordernumber}`}
                    />
                  </div>
                  <div className="font-medium">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span>{order.ordernumber}</span>
                      {/* R72: orderdatum + tijd zichtbaar in overzicht (Mark mail 6-mei). Bron: order.createdAt (orders_live_ls.createdat). */}
                      {order.createdAt && (
                        <span className="text-xs font-normal text-muted-foreground">
                          {formatOrderDateTime(order.createdAt)}
                        </span>
                      )}
                    </div>
                    <div className="md:hidden mt-1 flex flex-wrap items-center gap-1.5">
                      {/* R74: compacte type-badges (max-2 + overflow popover) */}
                      <BadgeRow items={getOrderTypes(order.suppliers)} variant="type" />
                      <BadgeStatus3D
                        status={order.status}
                        interactive={order.status !== "Aangemaakt" && hasPermission("ordersDecoprof", "write")}
                        onClick={() => {
                          if (order.status === "Aangemaakt") return;
                          updateStatus.mutate(
                            { ordernumber: order.ordernumber, status: "Aangemaakt" },
                            {
                              onSuccess: () => toast.success(`${order.ordernumber} → Aangemaakt`),
                              onError: (e: any) => toast.error(`Status update mislukt: ${e?.message ?? e}`),
                            },
                          );
                        }}
                      />
                    </div>
                    <div className="md:hidden mt-1">
                      {/* R74: compacte leveranciersbadges (max-2 + overflow popover) */}
                      <BadgeRow items={getOrderSuppliers(order.suppliers)} variant="supplier" itemMeta={supplierMeta} />
                    </div>
                  </div>
                  <div className="hidden md:block">
                    {/* R74: compacte type-badges (max-3 + overflow popover) */}
                    <BadgeRow items={getOrderTypes(order.suppliers)} variant="type" />
                  </div>
                  <div className="hidden md:block">
                    {/* R74: compacte leveranciersbadges (max-3 + overflow popover) */}
                    <BadgeRow items={getOrderSuppliers(order.suppliers)} variant="supplier" itemMeta={supplierMeta} />
                  </div>
                  <div className="hidden md:block">
                    <BadgeStatus3D
                      status={order.status}
                      interactive={order.status !== "Aangemaakt" && hasPermission("ordersDecoprof", "write")}
                      title={
                        order.status === "Aangemaakt"
                          ? "Reeds aangemaakt"
                          : "Klik om status naar Aangemaakt te zetten"
                      }
                      onClick={() => {
                        if (order.status === "Aangemaakt") return;
                        updateStatus.mutate(
                          { ordernumber: order.ordernumber, status: "Aangemaakt" },
                          {
                            onSuccess: () => toast.success(`${order.ordernumber} → Aangemaakt`),
                            onError: (e: any) => toast.error(`Status update mislukt: ${e?.message ?? e}`),
                          },
                        );
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Tile3DIconButton
                            aria-label={`Pakbon afdrukken voor ${order.ordernumber}`}
                            error={isFailed}
                            disabled={!canPrintPackingSlip || printingOrders.has(order.ordernumber) || order.orderid == null}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTilePrint(order);
                            }}
                          >
                            {printingOrders.has(order.ordernumber) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Printer className="h-[18px] w-[18px]" />
                            )}
                          </Tile3DIconButton>
                        </TooltipTrigger>
                        {(isFailed || !canPrintPackingSlip) && (
                          <TooltipContent>
                            {!canPrintPackingSlip
                              ? "Geen toegang tot Orders Decoprof"
                              : (printErrorReasons[order.ordernumber] ?? "Niet afgedrukt — klik om opnieuw te proberen")}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    {/* R75: chevron-dropdown verwijderd — had geen klik-functie (rij-klik toggelt expand). */}
                  </div>
                </div>
                {/* Smooth expand met grid-rows trick — geen layout-shift door late image-loads */}
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-300 ease-out",
                    isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                  style={{ overflowAnchor: "none" }}
                >
                  <div className="overflow-hidden">
                    {isExpanded && order.orderid != null && (
                      <div className="px-4 pb-4 pt-1" onClick={(e) => e.stopPropagation()}>
                        <OrderLinesExpansion orderid={order.orderid} ordernumber={order.ordernumber} />
                      </div>
                    )}
                  </div>
                </div>
              </Card3D>
            );
            };
            return <VirtualOrdersList orders={filteredOrders} renderOrder={renderOrder} flashing={flashing} />;
          })()
        )}
      </div>

      <ScrollHelpers />

      <ExportConfirmDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        total={sikkensSelectionPartition.all.length}
        alreadyExported={sikkensSelectionPartition.exported.length}
        onlyNewCount={sikkensSelectionPartition.fresh.length}
        onExportNew={() => {
          setExportDialogOpen(false);
          void runSikkensExport(sikkensSelectionPartition.fresh);
        }}
        onExportAll={() => {
          setExportDialogOpen(false);
          void runSikkensExport(sikkensSelectionPartition.all);
        }}
      />
    </div>
  );
};

/**
 * R52: window-virtualizer rondom de orderlijst.
 * - Gebruikt `useWindowVirtualizer` zodat de bestaande page-scroll (en de
 *   ScrollHelpers FAB die window.scrollTo gebruikt) intact blijft.
 * - `measureElement` voor variabele rij-hoogte (uitgevouwen tegels).
 * - Behoudt selectie/expand-state: state leeft in de parent en is gekoppeld
 *   aan ordernumber, dus virtualisatie raakt het niet.
 */
interface VirtualOrdersListProps {
  orders: OrdersDecoprofRow[];
  renderOrder: (order: OrdersDecoprofRow) => React.ReactNode;
  flashing: Set<string>;
}

const VirtualOrdersList = ({ orders, renderOrder }: VirtualOrdersListProps) => {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [parentOffset, setParentOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      const el = parentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setParentOffset(rect.top + window.scrollY);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: orders.length,
    estimateSize: () => 104, // R56: 72 was structureel te laag; realistischer estimate voorkomt groeiend "onderaan"
    overscan: 5,
    scrollMargin: parentOffset,
    getItemKey: (index) => orders[index].ordernumber,
  });

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div ref={parentRef} className="relative" style={{ height: totalSize }}>
      {items.map((vi) => {
        const order = orders[vi.index];
        return (
          <div
            key={vi.key}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
              paddingBottom: 8, // mimics space-y-2
            }}
          >
            {renderOrder(order)}
          </div>
        );
      })}
    </div>
  );
};

export default OrdersDecoprofTab;
